// useBotSpeech.js (Fixed EOF-based Response Handling)
import { useEffect, useState } from "react";

const messageQueue = [];
let isPlaying = false;
let lastBotMessageRef = null;
let countdownComplete = false;

// FIXED: Enhanced first message handling
let isFirstMessage = true;
let isWaitingForEOF = false;
let firstMessagePlayed = false; // Added missing variable declaration

function isPnloPanel() {
  try {
    const panelDetails = JSON.parse(
      sessionStorage.getItem("panelDetails") || "{}",
    );
    const panel = (panelDetails?.panel || "PNLO").toUpperCase();
    return panel === "PNLO";
  } catch {
    return true; // default to PNLO behavior if parse fails
  }
}
function processQueue() {
  // FIXED: Added missing condition check
  if (!countdownComplete) {
    return;
  }

  if (window.__questionnaireMode) {
    return;
  }

  // Hold bot audio while low-battery sound is playing
  if (window.__isLowBatteryAudioPlaying) {
    return;
  }

  if (isPlaying) return;

  if (messageQueue.length === 0) {
    // FIXED: Different logic for first message vs subsequent messages
    if (!isWaitingForEOF) {
      // FIXED: For first message, dispatch immediately after audio finishes
      if (isFirstMessage && firstMessagePlayed) {
        window.dispatchEvent(new CustomEvent("bot-queue-empty"));
        isFirstMessage = false; // Mark first message as complete
      }
      // For subsequent messages, only dispatch after EOF
      else if (!isFirstMessage) {
        window.dispatchEvent(new CustomEvent("bot-queue-empty"));
      }
    }
    return;
  }

  // FIXED: If waiting for EOF and queue has messages, continue processing
  if (isWaitingForEOF) {
    isWaitingForEOF = false;
  }

  isPlaying = true;
  const { transcript, audioBlob, audioFormat, speakerId, speakerName } =
    messageQueue.shift();

  window.dispatchEvent(new CustomEvent("bot-started-speaking"));
  if (speakerId || speakerName) {
    window.dispatchEvent(
      new CustomEvent("panel-speaking", {
        detail: { speakerId: speakerId || speakerName },
      }),
    );
  }

  window.dispatchEvent(
    new CustomEvent("bot-queue-status", {
      detail: {
        queueLength: messageQueue.length,
        isPlaying: true,
        isWaitingForEOF: isWaitingForEOF,
      },
    }),
  );

  playBotMessage(transcript, audioBlob, audioFormat, () => {
    isPlaying = false;

    // FIXED: Mark first message as played
    if (isFirstMessage) {
      firstMessagePlayed = true;
    }

    window.dispatchEvent(
      new CustomEvent("bot-queue-status", {
        detail: {
          queueLength: messageQueue.length,
          isPlaying: false,
          isWaitingForEOF: isWaitingForEOF,
        },
      }),
    );

    // Continue processing the queue
    const shouldDelay =
      isFirstMessage && !isPnloPanel() && messageQueue.length > 0;

    if (shouldDelay) {
      setTimeout(() => {
        processQueue();
      }, 2000); // 10 second delay
    } else {
      processQueue();
    }
  });
}

function playBotMessage(transcript, audioBlob, audioFormat, onDone) {
  if (audioBlob instanceof Blob) {
    try {
      const audioURL = URL.createObjectURL(audioBlob);

      //  STOP previous bot audio (prevents overlap)
      const oldAudio = document.getElementById("botAudio");
      if (oldAudio) {
        try {
          oldAudio.pause();
          oldAudio.currentTime = 0;
        } catch (e) {}
        if (oldAudio.parentNode) oldAudio.remove();
      }

      //  Create fresh audio element
      const audioElement = new Audio();
      audioElement.id = "botAudio";
      audioElement.crossOrigin = "anonymous";
      audioElement.autoplay = true;
      document.body.appendChild(audioElement);
      audioElement.src = audioURL;

      const timeoutId = setTimeout(() => {
        console.warn("⏱Playback stuck, force finish.");
        clearTimeout(timeoutId);
        URL.revokeObjectURL(audioURL);
        window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
        window.dispatchEvent(new CustomEvent("panel-speaking-ended"));

        //  cleanup
        if (audioElement.parentNode) audioElement.remove();

        onDone();
      }, 30000); //

      audioElement.onended = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(audioURL);
        window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
        window.dispatchEvent(new CustomEvent("panel-speaking-ended"));

        if (audioElement.parentNode) audioElement.remove();

        onDone();
      };

      audioElement.onerror = (error) => {
        console.error(" Audio playback error:", error);
        clearTimeout(timeoutId);
        URL.revokeObjectURL(audioURL);
        window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
        window.dispatchEvent(new CustomEvent("panel-speaking-ended"));

        if (audioElement.parentNode) audioElement.remove();

        onDone();
      };

      audioElement.play().catch((err) => {
        console.error("Failed to play audio", err);
        clearTimeout(timeoutId);
        URL.revokeObjectURL(audioURL);
        window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
        window.dispatchEvent(new CustomEvent("panel-speaking-ended"));

        if (audioElement.parentNode) audioElement.remove();

        onDone();
      });
    } catch (error) {
      console.error("Exception during audio playback", error);
      window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
      window.dispatchEvent(new CustomEvent("panel-speaking-ended"));
      onDone();
    }
  } else {
    console.warn(" Skipped TTS - No audioBlob provided");
    window.dispatchEvent(new CustomEvent("bot-finished-speaking"));
    window.dispatchEvent(new CustomEvent("panel-speaking-ended"));
    onDone();
  }
}

function handleBotResponse(e) {
  if (window.__questionnaireMode) {
    return;
  }

  const { transcript, audioBlob, audioFormat, speakerId, messageId } = e.detail;

  // FIXED: Handle EOF messages properly
  if (transcript === "EOF") {
    isWaitingForEOF = false;

    // FIXED: Only dispatch queue empty for subsequent messages (not first)
    if (!isFirstMessage) {
      window.dispatchEvent(new CustomEvent("bot-queue-empty"));
    } else {
    }
    return; // Don't queue EOF messages
  }

  // FIXED: Check if questionnaire is active and intercept AFTER queueing
  if (window.__questionnaireActive) {
    // Still queue the message but don't play it yet
    messageQueue.push({ transcript, audioBlob, audioFormat, speakerId });

    return;
  }

  const key =
    messageId !== undefined && messageId !== null
      ? `id:${messageId}`
      : transcript + (audioBlob ? audioBlob.size : "");
  if (key === lastBotMessageRef) {
    return;
  }

  lastBotMessageRef = key;
  messageQueue.push({
    transcript,
    audioBlob,
    audioFormat,
    speakerId: e.detail.speakerId,
    speakerName: e.detail.speakerName,
  });

  // FIXED: Different handling for first vs subsequent messages
  if (!isFirstMessage) {
    isWaitingForEOF = true;
  } else {
  }

  window.dispatchEvent(
    new CustomEvent("bot-queue-status", {
      detail: {
        queueLength: messageQueue.length,
        isPlaying: isPlaying,
        isWaitingForEOF: isWaitingForEOF,
      },
    }),
  );

  processQueue();
}

function handleCountdownComplete() {
  countdownComplete = true;

  if (messageQueue.length > 0) {
    processQueue();
  }
}

// Function to check if bot is truly busy
export const getBotQueueStatus = () => {
  return {
    queueLength: messageQueue.length,
    isPlaying: isPlaying,
    isWaitingForEOF: isWaitingForEOF,
    isEmpty: messageQueue.length === 0 && !isPlaying && !isWaitingForEOF,
    isBusy: messageQueue.length > 0 || isPlaying || isWaitingForEOF,
    isFirstMessage: isFirstMessage,
    firstMessagePlayed: firstMessagePlayed,
  };
};

const useBotSpeech = () => {
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [queueStatus, setQueueStatus] = useState({
    queueLength: 0,
    isPlaying: false,
    isWaitingForEOF: false,
  });

  useEffect(() => {
    window.addEventListener("bot-response", handleBotResponse);
    window.addEventListener("countdown-complete", handleCountdownComplete);

    const handleBotStartedSpeaking = () => setIsBotSpeaking(true);
    const handleBotFinishedSpeaking = () => setIsBotSpeaking(false);
    const handleQueueStatus = (event) => {
      setQueueStatus(event.detail);
    };

    window.addEventListener("bot-started-speaking", handleBotStartedSpeaking);
    window.addEventListener("bot-finished-speaking", handleBotFinishedSpeaking);
    window.addEventListener("bot-queue-status", handleQueueStatus);

    const handleResumeBotQueue = () => {
      countdownComplete = true;
      processQueue();
    };
    window.addEventListener("resume-bot-queue", handleResumeBotQueue);

    return () => {
      window.removeEventListener("bot-response", handleBotResponse);
      window.removeEventListener(
        "bot-started-speaking",
        handleBotStartedSpeaking,
      );
      window.removeEventListener(
        "bot-finished-speaking",
        handleBotFinishedSpeaking,
      );
      window.removeEventListener("countdown-complete", handleCountdownComplete);
      window.removeEventListener("bot-queue-status", handleQueueStatus);
      window.removeEventListener("resume-bot-queue", handleResumeBotQueue);
    };
  }, []);

  return {
    isBotSpeaking,
    queueStatus,
    // FIXED: Updated to properly reflect busy state
    isBotBusy:
      queueStatus.queueLength > 0 ||
      queueStatus.isPlaying ||
      queueStatus.isWaitingForEOF,
  };
};

export default useBotSpeech;
